import type { LiveMenuOption, MenuOptionJson, MenuOptionType } from './types'
import { optionIdFromLabel } from './slug'

const VALID_TYPES: MenuOptionType[] = ['checklist', 'radio', 'frequency', 'text']

function isStructuredOption(value: unknown): value is MenuOptionJson {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    typeof (value as MenuOptionJson).type === 'string' &&
    VALID_TYPES.includes((value as MenuOptionJson).type)
  )
}

function normalizeOptionPrices(choices: string[], prices?: number[]): number[] {
  return choices.map((_, i) =>
    Array.isArray(prices) && Number.isFinite(prices[i]) ? Number(prices[i]) : 0,
  )
}

export function normalizeOptionJson(
  raw: MenuOptionJson,
  itemMenuKey: string,
  index: number,
): LiveMenuOption {
  const type = raw.type === 'frequency' ? 'radio' : raw.type
  const label = (raw.label ?? '').trim() || `Option ${index + 1}`
  const id =
    raw.id?.trim() ||
    optionIdFromLabel(label, index) ||
    `opt-${itemMenuKey}-${index}`
  if (type === 'text') {
    return { id, label, type: 'text', choices: [], choicePrices: [] }
  }
  const choices = (raw.choices ?? []).map((c) => String(c).trim()).filter(Boolean)
  const choicePrices = normalizeOptionPrices(choices, raw.choicePrices)
  return { id, label, type, choices, choicePrices }
}

export function normalizeItemOptionsJson(
  raw: MenuOptionJson[] | string[] | undefined,
  itemMenuKey: string,
): LiveMenuOption[] {
  if (!Array.isArray(raw) || raw.length === 0) return []
  const first = raw[0]
  if (typeof first === 'string') {
    return (raw as string[]).map((label, i) =>
      normalizeOptionJson(
        { label, type: 'checklist', choices: [label] },
        itemMenuKey,
        i,
      ),
    )
  }
  if (isStructuredOption(first)) {
    return (raw as MenuOptionJson[]).map((opt, i) =>
      normalizeOptionJson(opt, itemMenuKey, i),
    )
  }
  return []
}

/** Upgrade stored options (with or without id) to LiveMenuOption[]. */
export function normalizeStoredOptions(
  opts: LiveMenuOption[] | string[] | undefined,
  itemId: string,
): LiveMenuOption[] {
  if (!Array.isArray(opts) || opts.length === 0) return []
  const first = opts[0]
  if (typeof first === 'string') {
    return normalizeItemOptionsJson(opts as string[], itemId)
  }
  if (typeof first === 'object' && first !== null && 'type' in first) {
    return (opts as LiveMenuOption[]).map((opt, i) =>
      normalizeOptionJson(
        {
          id: opt.id,
          label: opt.label,
          type: opt.type,
          choices: opt.choices,
          choicePrices: opt.choicePrices,
        },
        itemId,
        i,
      ),
    )
  }
  return []
}
